import * as ToneJS from "tone";
import { RydInstrument, RydInstrumentDescription, RydInternalInstrument } from "../../model/instrument";
import { Note, NoteVelocity } from "../../model/core";
import { TrackOutputType } from "../../model/tracks";

// /home/john/Desktop/workspace/eurydice/view/app/src/instruments/R303Drum.ts

export type DrumTime = number | string | undefined;

export type TR808DrumOptions = {
    volumeDb?: number;
    voices?: {
        kick: { volume: number; decay: number; pitchDecay: number; toneHz: number };
        snare: { volume: number; decay: number; toneHz: number };
        rim: { volume: number; decay: number; toneHz: number };
        ch: { volume: number; decay: number; toneHz: number };
        oh: { volume: number; decay: number; toneHz: number };
        lt: { volume: number; decay: number; toneHz: number };
        mt: { volume: number; decay: number; toneHz: number };
        ht: { volume: number; decay: number; toneHz: number };
        clap: { volume: number; decay: number; spread: number };
        cy: { volume: number; decay: number; toneHz: number };
        cowbell: { volume: number; decay: number; toneHz: number };
    };
    connectToDestination?: boolean;
    inputPitchShiftSemitones?: number;
}

/**
 * R303Drum: 808-style multi-voice drum module built on Tone.js
 * - Voices: kick, snare, clap, closed hat, open hat, low/mid/high tom, cowbell
 * - Exposes per-voice trigger methods and a single summed output
 */
export class TR808Drum implements RydInternalInstrument {
    id = 'TR808Drum';
    name = 'TrAcht Drum Machine';
    readonly outputNode: ToneJS.Gain;
    private master: ToneJS.Volume;


    // Kick + transient click
    private kick: ToneJS.MembraneSynth;
    private kickGain: ToneJS.Gain;
    private kickClick: ToneJS.NoiseSynth;
    private kickClickFilter: ToneJS.Filter;
    private kickClickGain: ToneJS.Gain;

    // Snare: noise + body
    private snNoise: ToneJS.NoiseSynth;
    private snHP: ToneJS.Filter;
    private snLP: ToneJS.Filter;
    private snNoiseGain: ToneJS.Gain;
    private snBody: ToneJS.MembraneSynth;
    private snBodyGain: ToneJS.Gain;

    private rim: ToneJS.MembraneSynth;
    private rimGain: ToneJS.Gain;

    // Clap: bandpassed noise + short multitap feedback
    private clapNoise: ToneJS.NoiseSynth;
    private clapBP: ToneJS.Filter;
    private clapHP: ToneJS.Filter;
    private clapDelay: ToneJS.FeedbackDelay;
    private clapGain: ToneJS.Gain;

    // Hats
    private ch: ToneJS.MetalSynth;
    private chGain: ToneJS.Gain;
    private oh: ToneJS.MetalSynth;
    private ohGain: ToneJS.Gain;

    // Toms
    private lt: ToneJS.MembraneSynth;
    private ltGain: ToneJS.Gain;
    private mt: ToneJS.MembraneSynth;
    private mtGain: ToneJS.Gain;
    private ht: ToneJS.MembraneSynth;
    private htGain: ToneJS.Gain;

    // Cowbell
    private cow: ToneJS.MetalSynth;
    private cowGain: ToneJS.Gain;

    // Cymbal
    private cy: ToneJS.MetalSynth;
    private cyDelay: ToneJS.FeedbackDelay;
    private cyGain: ToneJS.Gain;

    public cfg: Required<TR808DrumOptions>["voices"];
    public inputPitchShiftSemitones: number = 0;

    constructor(opts: TR808DrumOptions = {}) {
        // defaults
        this.cfg = {
            kick: { volume: 0.95, decay: 0.8, pitchDecay: 0.06, toneHz: 55 }, // A1-ish
            snare: { volume: 0.85, decay: 0.18, toneHz: 180 },
            rim: { volume: 0.4, decay: 0.12, toneHz: 180 },
            clap: { volume: 0.78, decay: 0.28, spread: 0.012 },
            ch: { volume: 0.6, decay: 0.05, toneHz: 4000 },
            oh: { volume: 0.55, decay: 0.35, toneHz: 4000 },
            lt: { volume: 0.7, decay: 0.35, toneHz: 110 },
            mt: { volume: 0.7, decay: 0.32, toneHz: 147 },
            ht: { volume: 0.7, decay: 0.28, toneHz: 196 },
            cy: { volume: 0.5, decay: 1.2, toneHz: 5300 },
            cowbell: { volume: 0.55, decay: 0.15, toneHz: 530 },
            ...opts.voices
        };

        this.outputNode = new ToneJS.Gain(1);
        this.master = new ToneJS.Volume(opts.volumeDb ?? 0);
        if (opts.connectToDestination === true) {
            console.log('TR808Drum: connecting to destination', opts);
            this.outputNode.chain(this.master, ToneJS.getDestination());
        } else {
            console.log('TR808Drum: not connecting to destination. using', this.master);
            this.outputNode.disconnect();
            this.outputNode.connect(this.master);
        }

        // input pitch shifter
        this.inputPitchShiftSemitones = opts.inputPitchShiftSemitones ?? 0;

        // Kick
        this.kick = new ToneJS.MembraneSynth({
            pitchDecay: this.cfg.kick.pitchDecay,
            octaves: 10,
            oscillator: { type: "sine" },
            envelope: { attack: 0.001, decay: this.cfg.kick.decay, sustain: 0, release: 0.1 },
        });
        this.kickGain = new ToneJS.Gain(this.cfg.kick.volume);

        // Kick click transient (HP-filtered short noise)
        this.kickClick = new ToneJS.NoiseSynth({
            noise: { type: "white" },
            envelope: { attack: 0.001, decay: 0.018, sustain: 0, release: 0 },
        });
        this.kickClickFilter = new ToneJS.Filter({ type: "highpass", frequency: 1600, Q: 0.7 });
        this.kickClickGain = new ToneJS.Gain(0.15);

        this.kick.connect(this.kickGain).connect(this.outputNode);
        this.kickClick.chain(this.kickClickFilter, this.kickClickGain, this.outputNode);

        // Rim
        this.rim = new ToneJS.MembraneSynth({
            pitchDecay: 0.01,
            octaves: 1,
            oscillator: { type: "triangle" },
            envelope: { attack: 0.001, decay: this.cfg.rim.decay, sustain: 0, release: 0.02 },
        });
        this.rimGain = new ToneJS.Gain(this.cfg.rim.volume);
        this.rim.connect(this.rimGain).connect(this.outputNode);


        // Snare
        this.snNoise = new ToneJS.NoiseSynth({
            noise: { type: "white" },
            envelope: { attack: 0.001, decay: this.cfg.snare.decay, sustain: 0, release: 0.02 },
        });
        this.snHP = new ToneJS.Filter({ type: "highpass", frequency: 1800, Q: 0.8 });
        this.snLP = new ToneJS.Filter({ type: "lowpass", frequency: 6500, Q: 0.6 });
        this.snNoiseGain = new ToneJS.Gain(this.cfg.snare.volume);

        this.snBody = new ToneJS.MembraneSynth({
            pitchDecay: 0.02,
            octaves: 1,
            oscillator: { type: "triangle" },
            envelope: { attack: 0.001, decay: Math.min(0.12, this.cfg.snare.decay), sustain: 0 },
        });
        this.snBodyGain = new ToneJS.Gain(0.35);

        this.snNoise.chain(this.snHP, this.snLP, this.snNoiseGain, this.outputNode);
        this.snBody.connect(this.snBodyGain).connect(this.outputNode);

        // Clap
        this.clapNoise = new ToneJS.NoiseSynth({
            noise: { type: "white" },
            envelope: { attack: 0.001, decay: this.cfg.clap.decay, sustain: 0, release: 0.08 },
        });
        this.clapBP = new ToneJS.Filter({ type: "bandpass", frequency: 1300, Q: 1.2 });
        this.clapHP = new ToneJS.Filter({ type: "highpass", frequency: 600, Q: 0.7 });
        this.clapDelay = new ToneJS.FeedbackDelay(this.cfg.clap.spread, 0.42);
        this.clapGain = new ToneJS.Gain(this.cfg.clap.volume);
        this.clapNoise.chain(this.clapBP, this.clapHP, this.clapDelay, this.clapGain, this.outputNode);

        // Hats (MetalSynth approximates 808 hat cluster)
        this.ch = new ToneJS.MetalSynth({
            // frequency: this.cfg.ch.toneHz,
            envelope: { attack: 0.001, decay: this.cfg.ch.decay, release: 0.01 },
            harmonicity: 5.1,
            modulationIndex: 32,
            resonance: 6000,
            octaves: 1.5,
        });
        this.chGain = new ToneJS.Gain(this.cfg.ch.volume).connect(this.outputNode);
        this.ch.connect(this.chGain);

        this.oh = new ToneJS.MetalSynth({
            // frequency: this.cfg.oh.toneHz,
            envelope: { attack: 0.001, decay: this.cfg.oh.decay, release: 0.02 },
            harmonicity: 5.1,
            modulationIndex: 32,
            resonance: 4000,
            octaves: 1.5,
        });
        this.ohGain = new ToneJS.Gain(this.cfg.oh.volume).connect(this.outputNode);
        this.oh.connect(this.ohGain);

        // Toms
        const tomOpts = (decay: number) =>
            ({
                pitchDecay: 0.05,
                octaves: 5,
                oscillator: { type: "sine" },
                envelope: { attack: 0.001, decay, sustain: 0, release: 0.05 },
            }) as ToneJS.MembraneSynthOptions;

        this.lt = new ToneJS.MembraneSynth(tomOpts(this.cfg.lt.decay));
        this.ltGain = new ToneJS.Gain(this.cfg.lt.volume).connect(this.outputNode);
        this.lt.connect(this.ltGain);

        this.mt = new ToneJS.MembraneSynth(tomOpts(this.cfg.mt.decay));
        this.mtGain = new ToneJS.Gain(this.cfg.mt.volume).connect(this.outputNode);
        this.mt.connect(this.mtGain);

        this.ht = new ToneJS.MembraneSynth(tomOpts(this.cfg.ht.decay));
        this.htGain = new ToneJS.Gain(this.cfg.ht.volume).connect(this.outputNode);
        this.ht.connect(this.htGain);

        // Cymbal
        this.cy = new ToneJS.MetalSynth({
            // frequency: this.cfg.cy.toneHz,
            envelope: { attack: 0.001, decay: this.cfg.cy.decay, release: 0.01 },
            harmonicity: 1.5,
            modulationIndex: 64,
            resonance: 4000,
            octaves: 2.5,
        });
        this.cyDelay = new ToneJS.FeedbackDelay(0.25, 0.4);
        this.cyGain = new ToneJS.Gain(this.cfg.cy.volume).connect(this.outputNode);
        this.cy.chain(this.cyDelay, this.cyGain);


        // Cowbell
        this.cow = new ToneJS.MetalSynth({
            // frequency: this.cfg.cowbell.toneHz,
            envelope: { attack: 0.001, decay: this.cfg.cowbell.decay, release: 0.01 },
            harmonicity: 2.5,
            modulationIndex: 20,
            resonance: 3000,
            octaves: 1.5,
        });
        this.cowGain = new ToneJS.Gain(this.cfg.cowbell.volume).connect(this.outputNode);
        this.cow.connect(this.cowGain);
    }

    getInstrumentDescription() {
        return {
            id: 'TR808Drum',
            name: 'TrAcht Drum Machine'
        };
    }

    // Triggers
    triggerKick(time?: DrumTime, velocity = 1) {
        const t = time ?? ToneJS.now();
        // Kick tuned around A1, pitch envelope supplies the punch
        this.kick.triggerAttackRelease(this.cfg.kick.toneHz, "8n", t, velocity);
        this.kickClick.triggerAttackRelease("32n", t, Math.min(1, velocity * 0.8));
    }

    triggerSnare(time?: DrumTime, velocity = 1) {
        const t = time ?? ToneJS.now();
        this.snNoise.triggerAttackRelease(this.cfg.snare.decay, t, velocity);
        this.snBody.triggerAttackRelease(this.cfg.snare.toneHz, "32n", t, Math.min(1, velocity * 0.6));
    }

    triggerRim(time?: DrumTime, velocity = 1) {
        const t = time ?? ToneJS.now();
        this.rim.triggerAttackRelease(this.cfg.rim.toneHz, "32n", t, Math.min(1, velocity * 0.6));
    }

    triggerClap(time?: DrumTime, velocity = 1) {
        const t = time ?? ToneJS.now();
        this.clapNoise.triggerAttackRelease(this.cfg.clap.decay, t, velocity);
    }

    triggerClosedHat(time?: DrumTime, velocity = 1) {
        const t = time ?? ToneJS.now();
        this.ch.triggerAttackRelease(this.cfg.ch?.toneHz, "16n", t, velocity);
    }

    triggerOpenHat(time?: DrumTime, velocity = 1) {
        const t = time ?? ToneJS.now();
        this.oh.triggerAttackRelease(this.cfg.oh.toneHz, "8n", t, velocity);
    }

    triggerLowTom(time?: DrumTime, velocity = 1) {
        const t = time ?? ToneJS.now();
        this.lt.triggerAttackRelease(this.cfg.lt.toneHz, "16n", t, velocity);
    }

    triggerMidTom(time?: DrumTime, velocity = 1) {
        const t = time ?? ToneJS.now();
        this.mt.triggerAttackRelease(this.cfg.mt.toneHz, "16n", t, velocity);
    }

    triggerHighTom(time?: DrumTime, velocity = 1) {
        const t = time ?? ToneJS.now();
        this.ht.triggerAttackRelease(this.cfg.ht.toneHz, "16n", t, velocity);
    }

    triggerCymbal(time?: DrumTime, velocity = 1) {
        const t = time ?? ToneJS.now();
        this.cy.triggerAttackRelease(this.cfg.cy.toneHz, "1n", t, velocity);
    }

    triggerCowbell(time?: DrumTime, velocity = 1) {
        const t = time ?? ToneJS.now();
        this.cow.triggerAttackRelease(this.cfg.cowbell.toneHz, "16n", t, velocity);
    }

    noteOn(_note: Note, velocity: NoteVelocity, time?: DrumTime) {
        // input pitch shift
        const note = transpose(_note, this.inputPitchShiftSemitones);
        if (!note) return;
        // Map certain notes to drum triggerstry {
        try {
            switch (note.getName()) {
                case "C1": this.triggerKick(time, velocity / 127); break;
                case "D1": this.triggerSnare(time, velocity / 127); break;
                case "D#1": this.triggerRim(time, velocity / 127); break;
                case "F#1": this.triggerClap(time, velocity / 127); break;
                case "G#1": this.triggerClosedHat(time, velocity / 127); break;
                case "A#1": this.triggerOpenHat(time, velocity / 127); break;
                case "C2": this.triggerLowTom(time, velocity / 127); break;
                case "D2": this.triggerMidTom(time, velocity / 127); break;
                case "E2": this.triggerHighTom(time, velocity / 127); break;
                case "G2": this.triggerCowbell(time, velocity / 127); break;
                case "A2": this.triggerCymbal(time, velocity / 127); break;
                default:
                    console.warn(`TR808Drum: note ${note.getName()} has no assigned drum trigger`);
            }
        } catch (error) {
            console.error('TR808Drum: error in noteOn', error);
        }
    }

    noteOff(note: Note) {
        // No-op for now
    }

    triggerInSequence: (notes: Note[], duration: string, velocity: NoteVelocity, time: DrumTime) => void = (notes, _duration, velocity, time) => {
        notes.map(n => transpose(n, this.inputPitchShiftSemitones))
            .filter(n => n !== undefined)
            .forEach(n => this.noteOn(n!, velocity, time));
    };

    getOutputType: () => TrackOutputType = () => 'VSYNTH';
    getOutputNode(): ToneJS.OutputNode {
        return this.master;
    }

    // Utilities
    setMasterVolumeDb(db: number) {
        this.master.volume.value = db;
    }

    updateVoiceSettings(options: Partial<TR808DrumOptions["voices"]>) {
        if (options?.kick) {
            this.cfg.kick = { ...this.cfg.kick, ...options.kick };
            this.kick.set({
                pitchDecay: this.cfg.kick.pitchDecay,
                envelope: { decay: this.cfg.kick.decay },
            });
            this.kickGain.gain.value = this.cfg.kick.volume;
        }

        if (options?.snare) {
            this.cfg.snare = { ...this.cfg.snare, ...options.snare };
            this.snNoise.set({
                envelope: { decay: this.cfg.snare.decay },
            });
            this.snBody.set({
                envelope: { decay: Math.min(0.12, this.cfg.snare.decay) },
            });
            this.snNoiseGain.gain.value = this.cfg.snare.volume;

        }

        if (options?.rim) {
            this.cfg.rim = { ...this.cfg.rim, ...options.rim };
            this.rim.set({
                envelope: { decay: this.cfg.rim.decay },
            });
            this.rimGain.gain.value = this.cfg.rim.volume;

        }

        if (options?.clap) {
            this.cfg.clap = { ...this.cfg.clap, ...options.clap };
            this.clapNoise.set({
                envelope: { decay: this.cfg.clap.decay },
            });
            this.clapDelay.delayTime.value = this.cfg.clap.spread;
            this.clapGain.gain.value = this.cfg.clap.volume;
        }
        if (options?.ch) {
            this.cfg.ch = { ...this.cfg.ch, ...options.ch };
            this.ch.set({
                envelope: { decay: this.cfg.ch.decay },
            });
            this.chGain.gain.value = this.cfg.ch.volume;
        }
        if (options?.oh) {
            this.cfg.oh = { ...this.cfg.oh, ...options.oh };
            this.oh.set({
                envelope: { decay: this.cfg.oh.decay },
            });
            this.ohGain.gain.value = this.cfg.oh.volume;
        }
        if (options?.lt) {
            this.cfg.lt = { ...this.cfg.lt, ...options.lt };
            this.lt.set({
                envelope: { decay: this.cfg.lt.decay },
            });
            this.ltGain.gain.value = this.cfg.lt.volume;
        }
        if (options?.mt) {
            this.cfg.mt = { ...this.cfg.mt, ...options.mt };
            this.mt.set({
                envelope: { decay: this.cfg.mt.decay },
            });
            this.mtGain.gain.value = this.cfg.mt.volume;
        }
        if (options?.ht) {
            this.cfg.ht = { ...this.cfg.ht, ...options.ht };
            this.ht.set({
                envelope: { decay: this.cfg.ht.decay },
            });
            this.htGain.gain.value = this.cfg.ht.volume;
        }

        if (options?.cy) {
            this.cfg.cy = { ...this.cfg.cy, ...options.cy };
            this.cy.set({
                envelope: { decay: this.cfg.cy.decay },
            });
            this.cyGain.gain.value = this.cfg.cy.volume;
        }
        if (options?.cowbell) {
            this.cfg.cowbell = { ...this.cfg.cowbell, ...options.cowbell };
            this.cow.set({
                envelope: { decay: this.cfg.cowbell.decay },
            });
            this.cowGain.gain.value = this.cfg.cowbell.volume;
        }
    }

    connect(node: ToneJS.InputNode) {
        this.outputNode.connect(node);
    }

    disconnect(node?: ToneJS.InputNode) {
        this.outputNode.disconnect(node);
    }

    dispose() {
        const nodes: ToneJS.ToneAudioNode[] = [
            this.master, this.outputNode,
            this.kick, this.kickGain, this.kickClick, this.kickClickFilter, this.kickClickGain,
            this.snNoise, this.snHP, this.snLP, this.snNoiseGain, this.snBody, this.snBodyGain,
            this.rim, this.rimGain,
            this.clapNoise, this.clapBP, this.clapHP, this.clapDelay, this.clapGain,
            this.ch, this.chGain, this.oh, this.ohGain,
            this.lt, this.ltGain, this.mt, this.mtGain, this.ht, this.htGain,
            this.cy, this.cyDelay, this.cyGain,
            this.cow, this.cowGain,
        ];
        nodes.forEach(n => { try { n.dispose(); } catch { /* noop */ } });
    }
}

function transpose(note: Note, semitones: number): Note | undefined {
    if (semitones === 0) return note;
    return semitones > 0 ? note.up(semitones) : note.down(-semitones);
}

export default TR808Drum;